import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CaseloadOverview = ({ caseload, onStudentSelect }) => {
  const getPriorityColor = (level) => {
    switch (level) {
      case 'high': return 'bg-red-100 text-red-700 border-red-300';
      case 'medium': return 'bg-amber-100 text-amber-700 border-amber-300';
      case 'low': return 'bg-green-100 text-green-700 border-green-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const getEngagementIcon = (level) => {
    switch (level) {
      case 'high': return { name: 'TrendingUp', color: '#10b981' };
      case 'medium': return { name: 'Minus', color: '#f59e0b' };
      case 'low': return { name: 'TrendingDown', color: '#ef4444' };
      default: return { name: 'Minus', color: '#6b7280' };
    }
  };

  const formatLastActivity = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Il y a quelques minutes';
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    const diffDays = Math.floor(diffHours / 24);
    return `Il y a ${diffDays}j`;
  };

  return (
    <div className="space-y-4">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Icon name="Users" size={20} color="#3b82f6" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{caseload?.length}</p>
              <p className="text-sm text-gray-600">Étudiants Actifs</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <Icon name="AlertTriangle" size={20} color="#ef4444" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {caseload?.filter(s => s?.interventionRequired)?.length}
              </p>
              <p className="text-sm text-gray-600">Interventions Requises</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Icon name="Clock" size={20} color="#f59e0b" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {caseload?.filter(s => s?.priorityLevel === 'high')?.length}
              </p>
              <p className="text-sm text-gray-600">Priorité Haute</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Icon name="CheckCircle" size={20} color="#10b981" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {Math.round(caseload?.reduce((sum, s) => sum + s?.recommendationAccuracy, 0) / caseload?.length)}%
              </p>
              <p className="text-sm text-gray-600">Précision Moyenne</p>
            </div>
          </div>
        </div>
      </div>

      {/* Student Cards */}
      <div className="space-y-3">
        {caseload?.map((student) => {
          const engagementIcon = getEngagementIcon(student?.engagementLevel);
          
          return (
            <div
              key={student?.id}
              className="bg-white rounded-lg p-4 md:p-5 shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
            >
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                {/* Student Info */}
                <div className="flex items-center gap-4 flex-1">
                  <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold">
                    {student?.name?.split(' ')?.map(n => n?.[0])?.join('')}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900">{student?.name}</h3>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded border ${getPriorityColor(student?.priorityLevel)}`}>
                        {student?.priorityLevel === 'high' ? 'Haute' : student?.priorityLevel === 'medium' ? 'Moyenne' : 'Basse'}
                      </span>
                      {student?.interventionRequired && (
                        <span className="px-2 py-0.5 text-xs font-medium rounded bg-red-100 text-red-700 border border-red-300">
                          <Icon name="AlertCircle" size={12} className="inline mr-1" />
                          Intervention
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">{student?.grade}</p>
                    {student?.recentAlert && (
                      <p className="text-xs text-red-600 mt-1">
                        <Icon name="AlertTriangle" size={12} className="inline mr-1" />
                        {student?.recentAlert}
                      </p>
                    )}
                  </div>
                </div>

                {/* Metrics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <Icon name={engagementIcon?.name} size={16} color={engagementIcon?.color} />
                      <p className="text-xs text-gray-600">Engagement</p>
                    </div>
                    <p className="text-sm font-semibold text-gray-900 capitalize">{student?.engagementLevel}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-600 mb-1">Performance</p>
                    <p className="text-sm font-semibold text-gray-900">{student?.performanceScore}%</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-600 mb-1">Précision IA</p>
                    <p className="text-sm font-semibold text-gray-900">{student?.recommendationAccuracy}%</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-600 mb-1">Quiz</p>
                    <p className="text-sm font-semibold text-gray-900">{student?.quizCompletion}/15</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Eye"
                    onClick={() => onStudentSelect(student?.id)}
                  >
                    Voir Profil
                  </Button>
                </div>
              </div>

              {/* Footer Info */}
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                <div className="flex items-center gap-4 text-xs text-gray-600">
                  <span className="flex items-center gap-1">
                    <Icon name="Clock" size={14} />
                    {formatLastActivity(student?.lastActivity)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="MessageCircle" size={14} />
                    Communication: <span className="font-medium capitalize">{student?.parentCommunication}</span>
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CaseloadOverview;